<script setup lang="ts">
    import type { Schemas } from '#shopware';
    import { getProductName } from '@shopware/helpers';
    import { onMounted, ref, nextTick } from 'vue';
    import { MEDICE_STAFF_IDS } from '~/types/Groups';

    const props = defineProps<{
        product: Schemas['Product'];
        storyblokBody?: PagePdpStoryblok['body'];
        storyblokStory?: PagePdpStoryblok['storyWrapper'];
        storyblokCustomTabs?: PagePdpStoryblok['customTabs'];
        storyblokRecommendation?: PagePdpStoryblok['recommendation'];
    }>();
    const localePath = useLocalePath();
    const { formatLink } = useInternationalization(localePath);
    const { hfpPushSuccess, hfpPushError } = useNotificationHelpers();
    const { getErrorsCodes } = useCartNotification();

    const { t } = useI18n();
    const { urlSegment } = useLanguage();

    // eslint-disable-next-line
    const product: any = props.product;

    const { addToCart, isInCart, count, quantity } = useAddToCart(product);

    const addAmountToCart = ref(1);
    const addToCartProxy = ref();

    const customFields = ref();
    // eslint-disable-next-line
    const usps: any = ref([]);
    // eslint-disable-next-line
    const tableNw: any = ref([]);
    const tableNwHeadline = ref();
    const tableNwValue = ref();
    // eslint-disable-next-line
    const tableNwTitle: any = ref([]);
    // eslint-disable-next-line
    const tableNwText: any = ref([]);
    // eslint-disable-next-line
    const tableInfos: any = ref([]);
    // eslint-disable-next-line
    const tableInfosTitle: any = ref([]);
    // eslint-disable-next-line
    const tableInfosText: any = ref([]);

    const tableNWIndex = ref<Array<number>>([]);
    const tableNWHighestField = ref();
    const tableInfosIndex = ref<Array<number>>([]);
    const tableInfosHighestField = ref();

    const productManufacturer = ref();
    const productNumber = ref();
    const ean = ref();
    const bioProduct = ref(false);
    const hasDiscount = ref(false);

    const daysPassed = ref();

    customFields.value = product.customFields;
    productManufacturer.value = product.manufacturer?.name;
    productNumber.value = product.productNumber;
    ean.value = product.ean;

    if (product.calculatedCheapestPrice && product.calculatedPrice?.unitPrice) {
        if (product.calculatedPrice?.unitPrice - product.calculatedCheapestPrice?.unitPrice > 0) {
            hasDiscount.value = true;
        }
    }

    if (product.releaseDate) {
        const productDate = new Date('' + product.releaseDate + '');
        const dateNumber = Date.now() - productDate.getTime();
        const seconds = Math.floor(dateNumber / 1000);
        let interval = Math.floor(seconds / 31536000);
        interval = Math.floor(seconds / 86400);
        daysPassed.value = interval;
    }

    if (product.properties) {
        for (const element of product.properties) {
            if (element.name === 'Bio-Produkt') {
                bioProduct.value = true;
            }
        }
    }

    if (product.customFields) {
        const customFieldsObj = product.customFields;

        const customFieldEntries = Object.entries(customFieldsObj);
        customFieldEntries.map(([key, val]) => {
            if (key.startsWith('usp')) {
                // eslint-disable-next-line
                const array: any = [key, val];
                usps.value.push(array);
            } else if (key.startsWith('tb1') && key.endsWith('headline')) {
                tableNwHeadline.value = val;
            } else if (key.startsWith('tb1') && key.endsWith('value')) {
                tableNwValue.value = val;
            } else if (key.startsWith('tb1') && key.endsWith('title')) {
                tableNwTitle.value.push(val);
            } else if (key.startsWith('tb1') && key.endsWith('text')) {
                const array = [key, val];
                tableNwText.value.push(array);
            } else if (key.startsWith('tb2') && key.endsWith('title')) {
                tableInfosTitle.value.push(val);
            } else if (key.startsWith('tb2') && key.endsWith('text')) {
                const array = [key, val];
                tableInfosText.value.push(array);
            }
        });

        tableNwText.value.forEach((element: Array<string>) => {
            const slicedElement = element[0].slice(12, -5);
            const intElement = parseInt(slicedElement);
            tableNWIndex.value.push(intElement);
            tableNWHighestField.value = Math.max(...tableNWIndex.value);
        });

        tableInfosText.value.forEach((element: Array<string>) => {
            const slicedElement = element[0].slice(15, -5);
            const intElement = parseInt(slicedElement);
            tableInfosIndex.value.push(intElement);
            tableInfosHighestField.value = Math.max(...tableInfosIndex.value);
        });

        for (let i = 0; i < tableNWHighestField.value; i++) {
            let entry = [];
            // eslint-disable-next-line
            tableNwText.value.forEach((element: any) => {
                if (element[0] === 'tb1_nw_field' + tableNWIndex.value[i] + '_text') {
                    entry = [tableNwTitle.value[i], element[1]];
                    tableNw.value.push(entry);
                }
            });
        }

        for (let i = 0; i < tableInfosHighestField.value; i++) {
            let entry = [];
            // eslint-disable-next-line
            tableInfosText.value.forEach((element: any) => {
                if (element[0] === 'tb2_infos_field' + tableInfosIndex.value[i] + '_text') {
                    entry = [tableInfosTitle.value[i], element[1]];
                    tableInfos.value.push(entry);
                }
            });
        }
    }

    addToCartProxy.value = async () => {
        quantity.value = addAmountToCart.value;
        let hasError;
        await addToCart();
        getErrorsCodes()?.forEach((element) => {
            if (element.key.includes('Mitarbeiterrabatt')) {
                return;
            }

            hasError = true;
            hfpPushError(
                t(`errors.${element.messageKey}`, {
                    ...element,
                    quantity: quantity.value,
                    name: props.product.name,
                    stock: props.product.stock
                })
            );
        });
        if (!hasError) {
            hfpPushSuccess(t(`cart.messages.addedToCart`, { p: props.product.name }));
        }
        addAmountToCart.value = 1;
    };

    // eslint-disable-next-line
    const navigator: any = ref();
    const shareData = ref({});
    const shareProduct = ref();

    const exclusiveDelivery = product.productNumber.includes('MER0006');

    const { detailedUser, fetchDetailedUser } = useDetailedUser();

    const { width } = useWindowSize();
    const windowWidth = ref();

    const resizeHandler = () => {
        windowWidth.value = width.value;
    };

    onMounted(async () => {
        windowWidth.value = width.value;
        window.addEventListener('resize', resizeHandler);
        resizeHandler();

        if (product.categories.some((category) => category.name == 'MEDICE exklusiv')) {
            await fetchDetailedUser();

            if (!detailedUser.value || !MEDICE_STAFF_IDS.includes(detailedUser.value.groupId)) {
                log.debug(
                    '[ProductStatic.vue] User is not logged in or does not belong to the MEDICE staff group. Redirecting to home.'
                );
                navigateTo(formatLink(`/${urlSegment.value}`) as string);
            }
        }

        navigator.value = window.navigator;

        shareData.value = {
            title: product.name,
            text: 'Produkt der MEDICE Health Family',
            url: window.location
        };

        shareProduct.value = () => {
            if (navigator.value.share && navigator.value.canShare && navigator.value.canShare(shareData.value)) {
                navigator.value.share(shareData.value);
            }
        };
    });

    onUnmounted(() => {
        window.removeEventListener('resize', resizeHandler);
    });

    // order and create tabs
    const tabs = ref([
        { title: t('product.tabs.specification') }
        //{ title: 'Bewertungen' }
    ]);

    const setActiveTab = (tab: string) => {
        activeTab.value = tab;
        //make sure everything is up and ready in the DOM
        if (windowWidth.value < 768) {
            nextTick(() => {
                const tabContent = document.getElementById('tab-content');
                tabContent?.scrollIntoView({ behavior: 'smooth' });
            });
        }
    };

    if (product.customFields?.application_text) {
        tabs.value.push({ title: t('product.tabs.application') });
    }

    if (tableNw.value.length >= 1) {
        tabs.value.push({ title: t('product.tabs.nutrition') });
    }

    if (props.storyblokCustomTabs) {
        for (const tab of props.storyblokCustomTabs) {
            tabs.value.push({ title: tab.title });
        }
    }

    if (props.storyblokStory) {
        tabs.value.unshift({ title: 'Story' });
    }

    // //To be implemented
    // if (true) {
    //     tabs.value.push({ title: 'Bewertungen' });
    // }

    const activeTab = ref(tabs.value[0].title);
</script>

<template>
    <div class="max-width">
        <!-- eslint-disable vue/no-v-html -->
        <StickyWrapper />
        <div class="product-grid">
            <div
                v-if="product"
                class="product-gallery"
                :class="{ '--single-image': product.media?.length === 1 }"
            >
                <div class="badges">
                    <ProductDiscountBadge
                        v-if="hasDiscount"
                        :product="product"
                    />

                    <div
                        v-if="daysPassed <= 14"
                        class="new-badge"
                    >
                        {{ t('product.badge.new') }}
                    </div>

                    <img
                        v-if="bioProduct"
                        class="bio-badge"
                        src="@/assets/images/eu-organic-logo.jpg"
                        alt="EU Bio Logo"
                    />
                </div>

                <ProductGallery :product="product" />
            </div>

            <div class="product-description">
                <!-- Product info -->
                <client-only>
                    <div class="share">
                        <button @click="shareProduct">
                            <span>{{ t('product.share') }}</span>
                            <img
                                src="~/assets/icons/share.svg"
                                alt="share icon"
                                aria-hidden="true"
                            />
                        </button>
                    </div>
                </client-only>

                <div class="title">
                    <h1>{{ getProductName({ product }) }}</h1>
                </div>

                <p
                    v-if="customFields?.claim"
                    class="claim"
                >
                    {{ customFields.claim }}
                </p>

                <div
                    v-if="usps"
                    class="usps"
                >
                    <p
                        v-for="usp in usps"
                        :key="usp[0]"
                    >
                        <img
                            src="~/assets/icons/check.svg"
                            alt="checkmark-icon"
                            aria-hidden="true"
                        />
                        {{ usp[1] }}
                    </p>
                </div>

                <AnnouncementMessage v-if="exclusiveDelivery">
                    <p>
                        {{ t('product.preorder') }}
                    </p>
                </AnnouncementMessage>

                <div class="product-variants">
                    <ProductVariantConfigurator />

                    <div class="available-wrapper">
                        <p
                            class="available"
                            :class="{
                                '--not-available': product?.stock === 0,
                                '--few-available': product?.stock < 10 && product.stock !== 0
                            }"
                        >
                            {{
                                product?.stock >= 10
                                    ? t('product.available.available')
                                    : product?.stock < 10 && product?.stock !== 0
                                      ? t('product.available.fewAvailable')
                                      : t('product.available.notAvailable')
                            }}
                        </p>
                        <p v-if="product?.stock !== 0">-</p>
                        <p
                            v-if="product?.stock !== 0"
                            class="shipping"
                        >
                            {{ t('product.shipping.available') + ' ' }}
                            {{
                                product?.deliveryTime?.name
                                    ? t('product.shipping.in') + ' ' + product?.deliveryTime?.name
                                    : ''
                            }}
                        </p>
                    </div>

                    <div
                        v-if="product"
                        class="price-information"
                    >
                        <SharedListingProductPrice
                            :product="product"
                            disable-unit-price
                            class="price"
                        />

                        <div class="additional-cost">
                            <span>{{ t('product.tax') + ' ' }}</span>
                            <a
                                href="/footer/versandinformationen"
                                target="_blank"
                            >
                                {{ t('product.shipping.cost') }}
                            </a>
                        </div>

                        <ProductUnits
                            :product="product"
                            class="unit"
                        />
                    </div>

                    <div class="add-to-cart">
                        <div class="input-wrapper">
                            <label for="amount">{{ t('product.count') }}</label>
                            <input
                                v-model="addAmountToCart"
                                class="add-amount"
                                type="number"
                                :min="1"
                                :max="100"
                                :step="1"
                                data-testid="cart-product-qty-select"
                                name="amount"
                                :aria-label="t('product.countAria')"
                            />
                        </div>

                        <PrimaryButton
                            id="add-to-cart-button"
                            product-button
                            data-testid="add-to-cart-button"
                            :disabled="product?.stock === 0"
                            @click="addToCartProxy"
                        >
                            <span>{{ t('product.addToCart') }}</span>

                            <img
                                v-if="count === 0 && isInCart"
                                src="~/assets/icons/shopping-cart-white.svg"
                                :alt="t('productCard.altCart')"
                                aria-hidden="true"
                            />

                            <img
                                src="~/assets/icons/plus.svg"
                                :alt="t('productCard.altAdd')"
                                aria-hidden="true"
                            />
                        </PrimaryButton>
                    </div>
                    <div
                        v-if="product?.stock > 0 && product?.stock < 26"
                        class="invader"
                    >
                        <span>🔥</span>
                        <p v-if="product?.stock <= 10">
                            {{ t('product.invader.variable', { p: product.stock }) }}
                        </p>
                        <p v-if="product?.stock >= 11 && product?.stock <= 25">
                            {{ t('product.invader.lowStock') }}
                        </p>
                        <span>🔥</span>
                    </div>
                </div>
            </div>
        </div>

        <div class="details">
            <div
                v-if="customFields?.short_description"
                class="description"
                data-testid="shop-product-description"
            >
                <div>
                    <h2>
                        {{ t('product.description') }}
                    </h2>

                    <p v-html="customFields.short_description" />
                </div>
            </div>

            <div
                v-if="storyblokRecommendation?.length"
                class="recommendations-container"
            >
                <StoryblokComponentWrapper
                    v-for="blok in storyblokRecommendation"
                    :key="blok._uid"
                    :blok="blok"
                />
            </div>

            <div class="tabs-wrapper">
                <div
                    v-if="tabs.length > 1"
                    class="tabs"
                >
                    <PrimaryButton
                        v-for="(tab, index) in tabs"
                        :key="index"
                        class="tab"
                        product-button
                        :button-text="tab.title"
                        variant="secondary"
                        :is-active="tab.title === activeTab ? true : false"
                        :is-tab="true"
                        @click="setActiveTab(tab.title)"
                    />
                </div>

                <div
                    id="tab-content"
                    class="tab-content"
                    :class="{ '--single': tabs.length === 1 }"
                >
                    <Transition
                        name="tabs"
                        mode="in-out"
                    >
                        <!-- Optional -->
                        <div v-show="activeTab === 'Story'">
                            <!-- Storyblok Story slot -->
                            <template v-if="storyblokStory">
                                <StoryblokComponentWrapper
                                    v-for="blok in storyblokStory"
                                    :key="blok._uid"
                                    :blok="blok"
                                />
                            </template>
                        </div>
                    </Transition>
                    <Transition
                        name="tabs"
                        mode="in-out"
                    >
                        <!-- Muss -->
                        <div v-show="activeTab === t('product.tabs.specification')">
                            <table class="table">
                                <thead>
                                    <tr>
                                        <th>{{ t('product.tabs.tabContent.infoHeader') }}</th>
                                        <th
                                            aria-hidden="true"
                                            class="aria-hidden"
                                        >
                                            {{ t('product.tabs.tabContent.info') }}
                                        </th>
                                    </tr>
                                </thead>
                                <tbody>
                                    <tr
                                        v-for="entry in tableInfos"
                                        :key="entry"
                                    >
                                        <td>{{ entry[0] }}</td>
                                        <td>{{ entry[1] }}</td>
                                    </tr>
                                    <tr v-if="productManufacturer">
                                        <td>{{ t('product.tabs.tabContent.manufacturer') }}</td>
                                        <td>{{ productManufacturer }}</td>
                                    </tr>
                                    <tr v-if="productNumber">
                                        <td>{{ t('product.tabs.tabContent.number') }}</td>
                                        <td>{{ productNumber }}</td>
                                    </tr>
                                    <tr v-if="ean">
                                        <td>{{ t('product.tabs.tabContent.ean') }}</td>
                                        <td>{{ ean }}</td>
                                    </tr>
                                </tbody>
                            </table>
                        </div>
                    </Transition>
                    <Transition
                        name="tabs"
                        mode="in-out"
                    >
                        <!-- optional -->
                        <div
                            v-show="activeTab === t('product.tabs.application') && customFields.application_text"
                            class="hints"
                        >
                            <p v-html="customFields.application_text" />
                        </div>
                    </Transition>
                    <Transition
                        name="tabs"
                        mode="in-out"
                    >
                        <!-- optional -->
                        <div v-show="activeTab === t('product.tabs.nutrition') && tableNw && tableNw.length >= 1">
                            <table class="table">
                                <thead>
                                    <tr>
                                        <th>{{ tableNwHeadline }}</th>
                                        <th>{{ tableNwValue }}</th>
                                    </tr>
                                </thead>
                                <tbody>
                                    <tr
                                        v-for="entry in tableNw"
                                        :key="entry"
                                    >
                                        <td>{{ entry[0] }}</td>
                                        <td>{{ entry[1] }}</td>
                                    </tr>
                                </tbody>
                            </table>
                        </div>
                    </Transition>

                    <div v-if="storyblokCustomTabs">
                        <Transition
                            name="tabs"
                            mode="in-out"
                        >
                            <!-- optional, dynamic storyblok tabs -->
                            <div v-show="activeTab === storyblokCustomTabs[0]?.title">
                                <StoryblokComponentWrapper
                                    v-for="blok in storyblokCustomTabs[0]?.content"
                                    :key="blok._uid"
                                    :blok="blok"
                                />
                            </div>
                        </Transition>

                        <Transition
                            name="tabs"
                            mode="in-out"
                        >
                            <div v-show="activeTab === storyblokCustomTabs[1]?.title">
                                <StoryblokComponentWrapper
                                    v-for="blok in storyblokCustomTabs[1]?.content"
                                    :key="blok._uid"
                                    :blok="blok"
                                />
                            </div>
                        </Transition>
                    </div>
                </div>
            </div>
        </div>

        <ClientOnly>
            <CrosssellingSlider
                v-if="product.streamIds?.length > 0"
                :product="product"
            />
            <template #fallback>
                <div class="loading-skeleton --block --full-width"></div>
            </template>
        </ClientOnly>
    </div>
    <!-- Storyblok Default Slot -->
    <template v-if="props.storyblokBody">
        <StoryblokComponentWrapper
            v-for="blok in props.storyblokBody"
            :key="blok._uid"
            :blok="blok"
        />
    </template>
</template>

<style lang="scss" scoped>
    @import '~/assets/scss/detail-page/front-end-detail-page.scss';
    @import '~/assets/scss/basics/loading-skeleton.scss';
</style>
